import React, { useState, useEffect, useRef } from 'react'
import { AutomationTarget } from '../../../types'
import classnames from 'classnames/bind'
import styles from './styles.scss'

const cx = classnames.bind(styles)

const DEFAULT_SAVED_FADE_MS = 2500

export type SectionCardSaveStatus = 'idle' | 'saving' | 'saved' | 'error'

export interface SectionCardProps extends AutomationTarget {
  number?: number
  title?: string
  description?: string
  saveStatus?: SectionCardSaveStatus
  savedFadeMs?: number
  hideSectionDividers?: boolean
  className?: string
  borderless?: boolean
  children: React.ReactNode
}

/**
 * SaveIndicator — shows "Saving...", "✓ Saved" (then fades), or "✗ Error"
 */
const SaveIndicator: React.FC<{
  status?: SectionCardSaveStatus
  fadeMs: number
}> = ({ status, fadeMs }) => {
  const [visible, setVisible] = useState(false)
  const [displayStatus, setDisplayStatus] = useState<
    'saving' | 'saved' | 'error'
  >('saving')
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (status === 'saving') {
      setVisible(true)
      setDisplayStatus('saving')
      if (timerRef.current) clearTimeout(timerRef.current)
    } else if (status === 'saved') {
      setVisible(true)
      setDisplayStatus('saved')
      timerRef.current = setTimeout(() => {
        setVisible(false)
      }, fadeMs)
    } else if (status === 'error') {
      setVisible(true)
      setDisplayStatus('error')
      if (timerRef.current) clearTimeout(timerRef.current)
    } else {
      setVisible(false)
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [status, fadeMs])

  if (!visible) return null

  return (
    <span
      className={cx('save-indicator', {
        saving: displayStatus === 'saving',
        saved: displayStatus === 'saved',
        error: displayStatus === 'error',
      })}
    >
      {displayStatus === 'saving' && 'Saving...'}
      {displayStatus === 'saved' && '✓ Saved'}
      {displayStatus === 'error' && '✗ Error'}
    </span>
  )
}

/**
 * SectionCard — A card container for form/preference sections.
 *
 * Features:
 * - Optional numbered step indicator (circle with number)
 * - Title and description
 * - Auto-save status indicator (Saving → Saved → fades after 2.5s)
 * - Uses CardContainer internally for consistent card styling
 *
 * @example
 * <SectionCard number={1} title="Where are you looking?" saveStatus="saved">
 *   <LocationSection ... />
 * </SectionCard>
 */
const SectionCard: React.FC<SectionCardProps> = ({
  number,
  title,
  description,
  saveStatus = 'idle',
  savedFadeMs = DEFAULT_SAVED_FADE_MS,
  className,
  hideSectionDividers = false,
  borderless = false,
  elmId = 'section-card',
  children,
}) => {
  const hasHeader = title || number

  // TODO: Previously used CardContainer but it added a nested wrapper causing
  // double borders. Using a plain div gives full control over border/padding/radius
  // via the .section-card class and supports the borderless variant for modals.
  return (
    <div
      data-elm-id={elmId}
      className={cx('section-card', className, { borderless })}
    >
      {hasHeader && (
        <div className={cx('header')}>
          {number !== undefined && (
            <span className={cx('number')}>{number}</span>
          )}
          {title && <span className={cx('title')}>{title}</span>}
          <SaveIndicator status={saveStatus} fadeMs={savedFadeMs} />
        </div>
      )}
      {description && (
        <p
          className={cx('description', {
            'description-indented': number !== undefined,
          })}
        >
          {description}
        </p>
      )}
      <div
        className={cx('content', {
          'no-section-dividers': hideSectionDividers,
        })}
      >
        {children}
      </div>
    </div>
  )
}

export default SectionCard
